/**
 * Inject read-only `sowDataTable` nodes into the SOW editor's narrative tiptap doc,
 * positioned at the end of the section each data table belongs to. The node carries
 * only the dataKey; the editor renders it read-only. Strips any pre-existing
 * data-table nodes first, so it is idempotent (inject(inject(x)) deep-equals inject(x))
 * and never duplicates across edit sessions. The nodes are excluded from sowContent by
 * the shared serializer, so editing prose never persists the tables.
 */
import { parseContentToTiptap, slugifyHeading, stripSowAiSectionNodes, stripSowDataTableNodes, } from '@protoss/shared';
import { flattenParagraphText, iterateBodyBlocks, parseLogicalSections, whenValueIsTruthy, } from './logical-sections.js';
import { markdownToDocumentChildren } from './markdown-to-children.js';
function isDocumentHeading(child) {
    if (child.type !== 'paragraph')
        return false;
    if (child.heading && child.heading !== 'Title')
        return true;
    const text = (child.children ?? [])
        .map((r) => ('text' in r ? r.text : ''))
        .join('')
        .trim()
        .toLowerCase();
    return (text === 'meals and entertainment' ||
        text === 'airfare' ||
        text === 'lodging' ||
        text === 'ground transportation');
}
/**
 * Preserve required, fixed subsections declared in a contentBlock default when an older or
 * no-op-saved narrative contains only part of that block. Existing prose and existing subsection
 * edits win; only missing heading extents are appended from the fixed default. This keeps contract
 * boilerplate durable across the editor, View, and export without changing stored SOW content.
 */
export function mergeFixedDefaultSubsections(contract, narrative) {
    const binding = contract.bindings?.narrative;
    let out = narrative;
    for (const section of parseLogicalSections(contract)) {
        for (const block of [...iterateBodyBlocks(section.body)]) {
            if (block.type !== 'contentBlock' ||
                block.source === 'ai' ||
                typeof block.dataKey !== 'string' ||
                typeof block.default !== 'string' ||
                !block.default.trim()) {
                continue;
            }
            const leaf = block.dataKey.split('.').pop() ?? block.dataKey;
            const current = narrative[leaf] ?? narrative[block.dataKey];
            if (!Array.isArray(current) || current.length === 0)
                continue;
            const defaults = markdownToDocumentChildren(block.default, {
                headingLevelOffset: -1,
                headingColor: binding?.headingColor,
                headingColorMaxLevel: binding?.headingColorMaxLevel,
                headingColorAfterMaxLevel: binding?.headingColorAfterMaxLevel,
                headingBoldMaxLevel: binding?.headingBoldMaxLevel,
                headingSizeAfterMaxLevel: binding?.headingSizeAfterMaxLevel,
                headingShading: binding?.headingShading,
                tableHeaderFill: binding?.tableHeaderFill,
                tableHeaderColor: binding?.tableHeaderColor,
            });
            if (!defaults.some(isDocumentHeading))
                continue;
            const currentHeadingSlugs = new Set(current
                .filter(isDocumentHeading)
                .map((heading) => slugifyHeading(flattenParagraphText(heading))));
            const missing = [];
            for (let index = 0; index < defaults.length;) {
                const heading = defaults[index];
                if (!heading || !isDocumentHeading(heading)) {
                    index += 1;
                    continue;
                }
                let end = index + 1;
                while (end < defaults.length && !isDocumentHeading(defaults[end]))
                    end += 1;
                const slug = slugifyHeading(flattenParagraphText(heading));
                if (slug && !currentHeadingSlugs.has(slug))
                    missing.push(...defaults.slice(index, end));
                index = end;
            }
            if (missing.length > 0) {
                if (out === narrative)
                    out = { ...narrative };
                out[leaf] = [...current, ...missing];
            }
        }
    }
    return out;
}
/** Concatenated plain text of a tiptap node tree (for the AI "Updated" edit-detection). */
function collectTiptapText(nodes) {
    let out = '';
    const walk = (n) => {
        const text = n.text;
        if (typeof text === 'string')
            out += text;
        if (Array.isArray(n.content))
            n.content.forEach(walk);
    };
    nodes.forEach(walk);
    return out.trim();
}
function headingTextOf(node) {
    if (node.type !== 'heading')
        return null;
    return (node.content ?? [])
        .filter((c) => c.type === 'text')
        .map((c) => String(c.text ?? ''))
        .join('')
        .trim();
}
function headingLevelOf(node) {
    if (node.type !== 'heading')
        return null;
    const lvl = node.attrs?.level;
    return typeof lvl === 'number' ? lvl : 1;
}
function dynamicKeys(body) {
    return body
        .filter((c) => c?.type === 'dynamicBlock' && typeof c.dataKey === 'string')
        .map((c) => c.dataKey);
}
export function injectSowDataTables(doc, contract) {
    const stripped = stripSowDataTableNodes(doc);
    if (!stripped || !Array.isArray(stripped.content))
        return doc;
    const content = stripped.content;
    // Match headings by SLUG, not exact text. The editor narrative is GENERATED prose, so a
    // heading like "One Time Data Conversion" must still anchor to the contract section
    // "One-Time Data Conversion" - the same lenient slug match the narrative split and the
    // view use. Exact-text matching dropped most tables to the doc end when the generated
    // heading text differed (case/punctuation) from the contract heading.
    const editorSlugs = new Set(content
        .map(headingTextOf)
        .filter((t) => !!t)
        .map(slugifyHeading));
    const groups = new Map(); // keyed by slug
    let lastAnchor = null;
    for (const s of parseLogicalSections(contract)) {
        const slug = s.headingText ? slugifyHeading(s.headingText) : '';
        const inEditor = !!slug && editorSlugs.has(slug);
        if (inEditor)
            lastAnchor = { slug, level: s.headingLevel ?? 1 };
        const anchor = inEditor ? { slug, level: s.headingLevel ?? 1 } : lastAnchor;
        if (!anchor)
            continue;
        for (const dk of dynamicKeys(s.body)) {
            const g = groups.get(anchor.slug) ?? { level: anchor.level, keys: [] };
            if (!g.keys.some((k) => k.dataKey === dk)) {
                g.keys.push({ dataKey: dk, includeHeading: !inEditor });
            }
            groups.set(anchor.slug, g);
        }
    }
    if (groups.size === 0)
        return stripped;
    const nodeFor = (e) => ({
        type: 'sowDataTable',
        attrs: { dataKey: e.dataKey, includeHeading: e.includeHeading },
    });
    // Walk the doc; insert a section's table(s) right after the anchor section's own content -
    // i.e. before the NEXT heading of ANY level (a table is a direct child of its section, so it
    // belongs above any sub-section). Flushing only at a same-or-higher heading wrongly pushed a
    // table past its section's H2 sub-sections to the end of the whole H1 extent (e.g. the EPC
    // Resource Rate Card table landed under a later "Outside Business Hours" instead).
    const out = [];
    const inserted = new Set();
    let pending = null;
    const flush = () => {
        if (!pending)
            return;
        for (const e of pending.keys) {
            out.push(nodeFor(e));
            inserted.add(e.dataKey);
        }
        pending = null;
    };
    for (const block of content) {
        const ht = headingTextOf(block);
        const hl = headingLevelOf(block);
        if (ht !== null && hl !== null) {
            if (pending)
                flush(); // any following heading ends the anchor section's direct content
            out.push(block);
            const g = groups.get(slugifyHeading(ht));
            if (g)
                pending = { keys: g.keys };
            continue;
        }
        out.push(block);
    }
    flush();
    // Tables whose anchor heading wasn't found append at the end (never lost).
    for (const g of groups.values()) {
        for (const e of g.keys) {
            if (!inserted.has(e.dataKey))
                out.push(nodeFor(e));
        }
    }
    return { ...stripped, content: out };
}
/**
 * Assemble the COMPLETE editable narrative markdown from the contract structure + the
 * resolved narrative map (`sample.narrative`, keyed by section slug, incl. `<slug>_ai` for
 * AI sections). Emits every section's heading followed by its contentBlock prose (fixed AND
 * AI - merged into one editable narrative), in full contract order.
 *
 * This is what lets the SOW editor edit the WHOLE document and place read-only data tables
 * in their sections: the generated narrative the editor used before is a sparse SUBSET of
 * the contract's sections, so any section the generator skipped had no heading for a data
 * table to anchor to (tables fell to the doc end). Emitting every section's heading -
 * including tables-only sections - gives `injectSowDataTables` an anchor for each table.
 * dynamicBlocks (read-only tables) and `source:'ai'` contentBlocks (editable AI nodes) are
 * NOT emitted here - they inject as their own nodes (`injectSowDataTables` /
 * `injectSowAiSections`), so AI prose keeps its provenance (it lives in `aiSections`, not
 * sowContent).
 */
export function assembleNarrativeMarkdown(contract, narrative) {
    const out = [];
    for (const s of parseLogicalSections(contract)) {
        if (s.isPreamble || !s.headingText)
            continue;
        // Look through layout-table cells too: a contentBlock may be boxed in a static
        // table (e.g. the Development Summary cell) and must still reach the editor.
        const body = [...iterateBodyBlocks(s.body)];
        const proseChunks = [];
        for (const b of body) {
            if (b.type !== 'contentBlock' || typeof b.dataKey !== 'string')
                continue;
            if (b.source === 'ai')
                continue; // AI prose injects as an editable sowAiSection node.
            // Deal-document content (source:'document') is render-only source material - the
            // editor never emits it as editable prose (edits would not round-trip: the export
            // re-injects the document text from the deal's uploaded file on every render).
            if (b.source === 'document')
                continue;
            const leaf = b.dataKey.split('.').pop() ?? b.dataKey;
            const md = narrative[leaf] ?? narrative[b.dataKey];
            // Fall back to the contentBlock's `default` boilerplate when the deal has no generated
            // narrative for it - mirroring the View/Word/Google renderers - so the editor shows the
            // boilerplate too (a section with only a `default` no longer vanishes in edit mode).
            const text = typeof md === 'string' && md.trim()
                ? md.trim()
                : typeof b.default === 'string' && b.default.trim()
                    ? b.default.trim()
                    : '';
            if (text)
                proseChunks.push(text);
        }
        const hasTable = body.some((b) => b.type === 'dynamicBlock');
        // An AI section's heading must be emitted too, so `injectSowAiSections` has an anchor to
        // drop the editable AI node after - even when the section carries no fixed prose itself.
        const hasAi = body.some((b) => b.type === 'contentBlock' && b.source === 'ai');
        // Skip pure-boilerplate sections (no narrative prose, no table, no AI) - those are
        // template chrome (signature blocks, fixed legal paragraphs) rendered by the contract.
        if (proseChunks.length === 0 && !hasTable && !hasAi)
            continue;
        out.push(`${'#'.repeat(Math.max(1, Math.min(6, s.headingLevel ?? 1)))} ${s.headingText}`);
        out.push('');
        for (const chunk of proseChunks) {
            out.push(chunk, '');
        }
    }
    return out.join('\n').trim();
}
/**
 * Inject EDITABLE `sowAiSection` nodes (one per AI-generated contract section) into the
 * editor's narrative tiptap doc, positioned at the end of the section each AI block belongs
 * to. Each node carries the AI section `slug` and holds the AI prose as editable content
 * (parsed from `narrative['<slug>_ai']`). Strips any pre-existing AI nodes first, so it is
 * idempotent (strip-then-inject) - mirroring `injectSowDataTables`.
 *
 * The AI prose is kept OUT of `sowContent` (the shared serializer drops `sowAiSection`); it
 * round-trips through `generationFlags.aiSections` via `extractAiSectionsFromTiptap` on save.
 * This preserves the AI/fixed split (and so the `source:'ai'` band in View/Word/Google) while
 * the user edits the AI text inline. Run this on the FRESH assembly only (not per-render) -
 * once the user has edited, their draft already carries the AI nodes; re-injecting from
 * `narrative` would clobber the edits.
 */
export function injectSowAiSections(doc, contract, narrative) {
    const stripped = stripSowAiSectionNodes(doc);
    if (!stripped || !Array.isArray(stripped.content))
        return doc;
    const content = stripped.content;
    const editorSlugs = new Set(content
        .map(headingTextOf)
        .filter((t) => !!t)
        .map(slugifyHeading));
    // Anchor each AI section to its own heading in the editor (assembleNarrativeMarkdown emits
    // a heading for every AI section), falling back to the nearest present parent heading.
    const groups = new Map(); // keyed by slug
    let lastAnchor = null;
    for (const s of parseLogicalSections(contract)) {
        const slug = s.headingText ? slugifyHeading(s.headingText) : '';
        const inEditor = !!slug && editorSlugs.has(slug);
        if (inEditor)
            lastAnchor = { slug, level: s.headingLevel ?? 1 };
        const anchor = inEditor ? { slug, level: s.headingLevel ?? 1 } : lastAnchor;
        if (!anchor)
            continue;
        // iterateBodyBlocks: an AI block boxed inside a layout-table cell still injects.
        for (const b of [...iterateBodyBlocks(s.body)]) {
            if (b.type !== 'contentBlock' || b.source !== 'ai' || typeof b.dataKey !== 'string')
                continue;
            const leaf = b.dataKey.split('.').pop() ?? b.dataKey;
            const md = narrative[leaf] ?? narrative[b.dataKey];
            if (typeof md !== 'string' || !md.trim())
                continue; // no AI content -> nothing to inject
            const aiSlug = leaf.replace(/_ai$/, '');
            const g = groups.get(anchor.slug) ?? { level: anchor.level, entries: [] };
            if (!g.entries.some((e) => e.slug === aiSlug))
                g.entries.push({ slug: aiSlug, markdown: md });
            groups.set(anchor.slug, g);
        }
    }
    if (groups.size === 0)
        return stripped;
    const nodeFor = (e) => {
        const parsed = parseContentToTiptap(e.markdown);
        const inner = Array.isArray(parsed.content) && parsed.content.length > 0
            ? parsed.content
            : [{ type: 'paragraph' }];
        // Stamp the AI's generated plain text so the node view can show "Updated" when the user
        // hand-edits it (compared against the live text).
        return {
            type: 'sowAiSection',
            attrs: { slug: e.slug, originalText: collectTiptapText(inner) },
            content: inner,
        };
    };
    // Walk the doc; insert a section's AI node(s) right before the next heading of level
    // <= the anchor level (the end of the anchor's section), or at doc end.
    const out = [];
    const inserted = new Set();
    let pending = null;
    const flush = () => {
        if (!pending)
            return;
        for (const e of pending.entries) {
            out.push(nodeFor(e));
            inserted.add(e.slug);
        }
        pending = null;
    };
    for (const block of content) {
        const ht = headingTextOf(block);
        const hl = headingLevelOf(block);
        if (ht !== null && hl !== null) {
            if (pending && hl <= pending.level)
                flush();
            out.push(block);
            const g = groups.get(slugifyHeading(ht));
            if (g)
                pending = { entries: g.entries, level: g.level };
            continue;
        }
        out.push(block);
    }
    flush();
    // AI sections whose anchor heading wasn't found append at the end (never lost).
    for (const g of groups.values()) {
        for (const e of g.entries) {
            if (!inserted.has(e.slug))
                out.push(nodeFor(e));
        }
    }
    return { ...stripped, content: out };
}
/**
 * Heading texts of the contract's CONDITIONAL sections (the heading paragraph or any body
 * block carries `conditional`). The editor bands these to mirror the view's
 * `.dcv-gen-section--conditional`. Conditional sections' prose is fixed narrative, so it is
 * present in the editor (unlike AI sections, which are stored separately).
 */
export function conditionalSectionHeadings(contract) {
    const out = [];
    for (const s of parseLogicalSections(contract)) {
        if (!s.headingText)
            continue;
        const headingCond = !!s.heading?.conditional;
        const bodyCond = s.body.some((b) => b?.conditional === true);
        if (headingCond || bodyCond)
            out.push(s.headingText);
    }
    return out;
}
/**
 * All section heading texts (non-preamble) of a contract. The SOW editor LOCKS these as
 * read-only: each is a narrative split key (`slugifyHeading`), so editing one re-slugs and
 * orphans its body (and the View renders the heading from the contract regardless).
 */
export function sectionHeadings(contract) {
    const out = [];
    for (const s of parseLogicalSections(contract)) {
        if (s.isPreamble || !s.headingText)
            continue;
        out.push(s.headingText);
    }
    return out;
}
const CONTRACT_HEADING_LEVEL = {
    Title: 1,
    Heading1: 1,
    Heading2: 2,
    Heading3: 3,
    Heading4: 4,
    Heading5: 5,
    Heading6: 6,
};
function contractHeadingLevel(child) {
    if (child?.type === 'paragraph' && typeof child.heading === 'string') {
        return CONTRACT_HEADING_LEVEL[child.heading] ?? 1;
    }
    return null;
}
function contractHeadingText(child) {
    return (child?.children ?? [])
        .filter((c) => typeof c?.text === 'string')
        .map((c) => c.text)
        .join('')
        .trim();
}
/**
 * Remove the user-EXCLUDED conditional sections (by heading slug) from a contract before
 * render. A section's extent runs from its heading to the next heading of the SAME-or-higher
 * level, so removing an H1 ("Future State Systems Landscape") also drops its sub-sections.
 * Used identically by the View, the Word/Google export, and the editor so all surfaces agree.
 * No-op when `excluded` is empty. (Only `conditional:true` headings should be passed.)
 */
/**
 * Drop `when`-gated children whose guard resolves falsy for THIS deal.
 *
 * A contract may carry two mutually exclusive variants of a section - e.g. the time-and-materials
 * and fixed-fee "Fees and Payments" blocks, gated on `{{protoss:context.isFixedFee}}`. The docx
 * renderer evaluates `when` itself, but the SOW View, the editor, and every `parseLogicalSections`
 * consumer read the contract structurally and would otherwise show BOTH branches. Applying this
 * once, right where `filterExcludedSections` is applied, makes every downstream surface agree
 * without threading a predicate through each of them.
 *
 * `resolve` maps a token path (e.g. `context.isFixedFee`) to its resolved string. Deliberately NOT
 * applied by the template-authoring surfaces: an author editing the contract must still see every
 * branch, including ones this deal would not render.
 */
export function filterInactiveWhenBranches(contract, resolve) {
    const hasWhen = (contract.sections ?? []).some((s) => (s.children ?? []).some((c) => c.when !== undefined));
    if (!hasWhen)
        return contract;
    const sections = (contract.sections ?? []).map((section) => {
        const children = (section.children ?? []).filter((child) => {
            if (child.when === undefined)
                return true;
            const resolved = child.when.replace(/\{\{\s*protoss:([^}]+?)\s*\}\}/g, (_m, path) => resolve(path.trim()));
            return whenValueIsTruthy(resolved);
        });
        return { ...section, children };
    });
    return { ...contract, sections };
}
export function filterExcludedSections(contract, excluded) {
    if (!excluded || excluded.size === 0)
        return contract;
    const sections = (contract.sections ?? []).map((section) => {
        const children = (section.children ?? []);
        const out = [];
        let skipUntilLevel = null;
        for (const child of children) {
            const lvl = contractHeadingLevel(child);
            if (lvl !== null) {
                // A heading of the same-or-higher level ends the section we're skipping.
                if (skipUntilLevel !== null && lvl <= skipUntilLevel)
                    skipUntilLevel = null;
                if (skipUntilLevel === null && excluded.has(slugifyHeading(contractHeadingText(child)))) {
                    skipUntilLevel = lvl; // begin skipping this excluded section + its sub-sections
                    continue;
                }
            }
            if (skipUntilLevel === null)
                out.push(child);
        }
        return { ...section, children: out };
    });
    return { ...contract, sections };
}
/** Sentinel dataKey marking the injected read-only signature-block node (a `sowDataTable`). */
export const SIGNATURE_DATA_KEY = '__signature__';
/**
 * The contract's literal signature / acceptance table(s) + `signatureBlock` nodes - structural
 * chrome that is NOT part of the editable narrative (it carries data-driven tokens like the
 * client/company legal names), so the editor injects it as a read-only node and the View/
 * exports render it from the contract. Returns the contract children to render via DocContractView.
 */
export function findSignatureBlockChildren(contract) {
    const out = [];
    for (const section of contract.sections ?? []) {
        for (const child of (section.children ?? [])) {
            if (child.type === 'signatureBlock' || child.type === 'table')
                out.push(child);
        }
    }
    return out;
}
/**
 * Inject a read-only signature-block node (a `sowDataTable` with the SIGNATURE_DATA_KEY
 * sentinel) so the editor shows the Acceptance signature table (data-driven values) - static
 * contract chrome, not in the editable narrative. Anchors it AFTER the content of the section
 * that contains the signatureBlock (the "Acceptance" heading must be present in the editor, e.g.
 * because that section also carries an intro contentBlock), so the signature renders in its
 * contract position - e.g. before an Appendix - instead of clumping at the doc end. Falls back to
 * append-at-end when that heading isn't in the narrative (e.g. a signature-only last section).
 * No-op when the contract has none. Idempotent (strips any prior signature node first).
 */
export function injectSowSignatureBlock(doc, contract) {
    const d = doc;
    if (!d || !Array.isArray(d.content))
        return doc;
    if (findSignatureBlockChildren(contract).length === 0)
        return doc;
    const content = d.content.filter((n) => !(n?.type === 'sowDataTable' &&
        n.attrs?.dataKey === SIGNATURE_DATA_KEY));
    const sigNode = {
        type: 'sowDataTable',
        attrs: { dataKey: SIGNATURE_DATA_KEY, badge: 'Signature block - read-only' },
    };
    // The heading slug of the section that holds the signatureBlock (its anchor in the editor).
    let anchorSlug = null;
    for (const s of parseLogicalSections(contract)) {
        if (s.isPreamble || !s.headingText)
            continue;
        const hasSig = s.body.some((b) => b.type === 'signatureBlock' || b.type === 'table');
        if (hasSig) {
            anchorSlug = slugifyHeading(s.headingText);
            break;
        }
    }
    const editorSlugs = new Set(content
        .map(headingTextOf)
        .filter((t) => !!t)
        .map(slugifyHeading));
    if (!anchorSlug || !editorSlugs.has(anchorSlug)) {
        return { ...doc, content: [...content, sigNode] };
    }
    // Insert the signature right before the NEXT heading after the anchor section (i.e. at the end
    // of that section's content) - same placement rule as injectSowDataTables.
    const out = [];
    let pending = false;
    const flush = () => {
        if (pending) {
            out.push(sigNode);
            pending = false;
        }
    };
    for (const block of content) {
        const ht = headingTextOf(block);
        if (ht !== null) {
            if (pending)
                flush();
            out.push(block);
            if (slugifyHeading(ht) === anchorSlug)
                pending = true;
            continue;
        }
        out.push(block);
    }
    flush();
    return { ...doc, content: out };
}
/** Find the dynamicBlock definition for a dataKey (for the read-only renderer). */
export function findDynamicBlock(contract, dataKey) {
    for (const section of contract.sections ?? []) {
        for (const child of section.children ?? []) {
            if (child.type === 'dynamicBlock' &&
                child.dataKey === dataKey) {
                return child;
            }
        }
    }
    return null;
}
/**
 * The DocumentChild[] a read-only sowDataTable node should render for `dataKey`: the
 * dynamicBlock, optionally preceded by its nearest subsection heading paragraph (when
 * that heading isn't already in the narrative - so contract-only labels like
 * "Master Data" still show in the editor, without duplicating headings already there).
 */
export function findDataTableRenderChildren(contract, dataKey, includeHeading) {
    for (const section of contract.sections ?? []) {
        const children = (section.children ?? []);
        const idx = children.findIndex((c) => c.type === 'dynamicBlock' && c.dataKey === dataKey);
        if (idx < 0)
            continue;
        const block = children[idx];
        if (!includeHeading)
            return [block];
        for (let j = idx - 1; j >= 0; j--) {
            const p = children[j];
            if (p.type === 'dynamicBlock')
                break; // don't cross another table's section
            if (p.type === 'paragraph' && p.heading)
                return [p, block];
        }
        return [block];
    }
    return [];
}
//# sourceMappingURL=sow-editor-inject.js.map